/**
 * 应期的纯日期地基。
 *
 * 本模块只回答“下一次哪一天/哪一月符合给定历法条件”，不判断吉凶，
 * 不生成主应期，也不做置信度裁决。正式应期规则应在上层消费这些候选。
 */

import {
  BRANCHES, fourPillars, fromJD, termsOfYear, toJD, toJDN
} from './ganzhi.js';
import { SIX_COMBINE, SIX_HARM, clashes, combines, punishes, tombOf } from './analyze.js';
import { TIME_SCALE_THRESHOLDS } from './verdict-thresholds.js';

const TERM_EPSILON_DAYS = 2 / (24 * 60);
const FUTURE_MONTH_COUNT = 12;

function assertBranch(branch, label = '地支') {
  if (!BRANCHES.includes(branch)) throw new RangeError(`${label}必须是十二地支之一，收到 ${branch}`);
}

function assertGanzhi(ganzhi) {
  if (typeof ganzhi !== 'string' || ganzhi.length !== 2) {
    throw new RangeError(`干支日必须是两个字，收到 ${ganzhi}`);
  }
  // fourPillars 的日柱索引会做最终合法性校验；这里先挡住明显非法输入。
  const candidate = BRANCHES.includes(ganzhi[1]);
  if (!candidate) throw new RangeError(`干支日必须包含合法地支，收到 ${ganzhi}`);
}

function isRawPillars(value) {
  return Boolean(value?.day?.name && value?.month?.branch && value?.at);
}

function normalizeContext(value) {
  const at = value?.at || (
    Number.isInteger(value?.year) && Number.isInteger(value?.month) && Number.isInteger(value?.day)
      ? value
      : null
  );
  if (!at) {
    throw new TypeError('日期计算需要带 at 的 fourPillars()/analyze()/divine() 结果，或显式公历时刻');
  }
  const options = value?.calendarOptions || {};
  const raw = isRawPillars(value) ? value : fourPillars(at, options);
  return {
    at: {
      year: at.year,
      month: at.month,
      day: at.day,
      hour: at.hour ?? 12,
      minute: at.minute ?? 0
    },
    options,
    raw
  };
}

function pad(number) {
  return String(number).padStart(2, '0');
}

function dateString(moment) {
  return `${moment.year}-${pad(moment.month)}-${pad(moment.day)}`;
}

function momentAtJDN(jdn, at) {
  const date = fromJD(jdn);
  return {
    year: date.year,
    month: date.month,
    day: date.day,
    hour: at.hour,
    minute: at.minute
  };
}

function dayResult(jdn, daysAway, context) {
  const moment = momentAtJDN(jdn, context.at);
  const pillars = fourPillars(moment, context.options);
  return {
    date: dateString(moment),
    ganzhi: pillars.day.name,
    branch: pillars.day.branch,
    daysAway
  };
}

function strictlyNextOffset(currentIndex, targetIndex, cycle) {
  const distance = (targetIndex - currentIndex + cycle) % cycle;
  return distance === 0 ? cycle : distance;
}

/** 下一次指定地支值日；“next”严格指未来，若今日同支则返回十二日后。 */
export function nextBranchDay(branch, pillars) {
  assertBranch(branch);
  const context = normalizeContext(pillars);
  const current = BRANCHES.indexOf(context.raw.day.branch);
  const target = BRANCHES.indexOf(branch);
  const daysAway = strictlyNextOffset(current, target, 12);
  const jdn = toJDN(context.at.year, context.at.month, context.at.day) + daysAway;
  return dayResult(jdn, daysAway, context);
}

/** 下一次指定六十甲子日；若今日同干支则返回六十日后。 */
export function nextGanzhiDay(ganzhi, pillars) {
  assertGanzhi(ganzhi);
  const context = normalizeContext(pillars);
  // 复用四柱算法验证干支组合是否合法，并取得六十甲子序号。
  for (let offset = 1; offset <= 60; offset += 1) {
    const jdn = toJDN(context.at.year, context.at.month, context.at.day) + offset;
    const candidate = dayResult(jdn, offset, context);
    if (candidate.ganzhi === ganzhi) {
      return candidate;
    }
  }
  throw new RangeError(`不是合法的六十甲子组合：${ganzhi}`);
}

function futureTerms(context, yearSpan = 2) {
  const from = toJD(
    context.at.year,
    context.at.month,
    context.at.day,
    context.at.hour,
    context.at.minute
  );
  const terms = [];
  for (let year = context.at.year; year <= context.at.year + yearSpan; year += 1) {
    for (const term of termsOfYear(year, context.options.tzOffset ?? 8)) {
      if (term.jd > from) terms.push({ ...term, year });
    }
  }
  return terms.sort((a, b) => a.jd - b.jd);
}

function monthResult(term, context) {
  // 加两分钟跨过交节数值边界，避免四柱在浮点误差下仍落到旧月。
  const moment = fromJD(term.jd + TERM_EPSILON_DAYS);
  const at = {
    year: moment.year,
    month: moment.month,
    day: moment.day,
    hour: moment.hour,
    minute: moment.minute
  };
  const calculated = fourPillars(at, context.options);
  const daysAway = toJDN(at.year, at.month, at.day)
    - toJDN(context.at.year, context.at.month, context.at.day);
  return {
    date: dateString(at),
    at,
    ganzhi: calculated.day.name,
    monthGanzhi: calculated.month.name,
    branch: calculated.month.branch,
    term: term.name,
    daysAway
  };
}

/** 未来十二个月中进入指定月建的全部月份，按交节先后排列。 */
export function nextMonthBranches(branch, pillars) {
  assertBranch(branch, '月建');
  const context = normalizeContext(pillars);
  return futureTerms(context)
    .slice(0, FUTURE_MONTH_COUNT)
    .filter((candidate) => candidate.branch === branch)
    .map((term) => monthResult(term, context));
}

/** 下一次进入指定月建；若当前正是该月建，返回下一年的同名月建。 */
export function nextMonthBranch(branch, pillars) {
  const result = nextMonthBranches(branch, pillars)[0];
  if (!result) throw new RangeError(`未来十二个月内未找到月建 ${branch}`);
  return result;
}

/**
 * 从起算时刻之后，下一个进入指定年支的立春。
 * 首选签名 nextYearBranch(fromDate, branch)；为与本文件早期日期函数保持兼容，
 * 也接受 nextYearBranch(branch, fromDate)。“next”严格指未来：若当前已过
 * 该年立春且年支相同，返回十二年后。
 */
export function nextYearBranch(fromDateOrBranch, branchOrFromDate) {
  const branch = typeof fromDateOrBranch === 'string' ? fromDateOrBranch : branchOrFromDate;
  const fromDate = typeof fromDateOrBranch === 'string' ? branchOrFromDate : fromDateOrBranch;
  assertBranch(branch, '年支');
  const context = normalizeContext(fromDate);
  const from = toJD(
    context.at.year,
    context.at.month,
    context.at.day,
    context.at.hour,
    context.at.minute
  );
  for (let year = context.at.year; year <= context.at.year + 13; year += 1) {
    const lichun = termsOfYear(year, context.options.tzOffset ?? 8)[0];
    if (lichun.jd <= from) continue;
    const moment = fromJD(lichun.jd + TERM_EPSILON_DAYS);
    const at = {
      year: moment.year,
      month: moment.month,
      day: moment.day,
      hour: moment.hour,
      minute: moment.minute
    };
    const calculated = fourPillars(at, context.options);
    if (calculated.year.branch !== branch) continue;
    const daysAway = toJDN(at.year, at.month, at.day)
      - toJDN(context.at.year, context.at.month, context.at.day);
    return {
      date: dateString(at),
      at,
      ganzhi: calculated.day.name,
      yearGanzhi: calculated.year.name,
      branch: calculated.year.branch,
      term: '立春',
      daysAway
    };
  }
  throw new RangeError(`未来十三年内未找到年支 ${branch}`);
}

/**
 * 某支与日辰的纯关系。relations 可能同时含多项，例如辰逢辰既值日又自刑。
 */
export function branchRelationToDay(branch, dayOrPillars) {
  assertBranch(branch);
  const dayBranch = typeof dayOrPillars === 'string'
    ? dayOrPillars.at(-1)
    : normalizeContext(dayOrPillars).raw.day.branch;
  assertBranch(dayBranch, '日辰');
  const relations = [];
  if (branch === dayBranch) relations.push('值日');
  if (clashes(branch, dayBranch)) relations.push('冲');
  if (combines(branch, dayBranch)) relations.push('合');
  if (SIX_HARM[branch] === dayBranch) relations.push('害');
  if (punishes(branch, dayBranch)) relations.push('刑');
  return { branch, dayBranch, relations };
}

/** 旬空之爻：值日或被日辰冲，两个候选都保留。 */
export function escapeVoidDates(branch, pillars) {
  assertBranch(branch);
  const clashBranch = BRANCHES[(BRANCHES.indexOf(branch) + 6) % 12];
  return [
    { ...nextBranchDay(branch, pillars), rule: '值日' },
    { ...nextBranchDay(clashBranch, pillars), rule: '冲空' }
  ].sort((a, b) => a.daysAway - b.daysAway);
}

/** 月破之爻：实破值日、下一个不再冲该爻的月建，以及六合该爻之日。 */
export function escapeBreakDates(branch, pillars) {
  assertBranch(branch);
  const context = normalizeContext(pillars);
  const term = futureTerms(context).find((candidate) => !clashes(branch, candidate.branch));
  if (!term) throw new RangeError(`两年范围内未找到 ${branch} 的出破月`);
  const monthCandidate = monthResult(term, context);
  const fillCandidate = nextBranchDay(branch, pillars);
  const combineCandidate = nextBranchDay(SIX_COMBINE[branch], pillars);
  return [
    { ...fillCandidate, rule: '实破' },
    { ...monthCandidate, rule: '出破月' },
    { ...combineCandidate, rule: '合破' }
  ].sort((a, b) => a.daysAway - b.daysAway);
}

/** 入墓之爻：取冲开墓库地支之日；墓库只复用 analyze.js 的 tombOf()。 */
export function tombOpenDates(element, pillars) {
  const tomb = tombOf(element);
  if (!tomb) throw new RangeError(`五行必须是木火土金水之一，收到 ${element}`);
  const openBranch = BRANCHES[(BRANCHES.indexOf(tomb) + 6) % 12];
  return [{
    ...nextBranchDay(openBranch, pillars),
    tomb,
    rule: '冲墓'
  }];
}

/**
 * 把多组候选按公历日期合并；同日只归并 rule/source，不加权、不打分。
 */
export function mergeCandidates(lists) {
  if (!Array.isArray(lists) || lists.some((list) => !Array.isArray(list))) {
    throw new TypeError('mergeCandidates() 需要候选数组的数组');
  }
  const byDate = new Map();
  for (const candidate of lists.flat()) {
    if (!candidate?.date || !candidate?.ganzhi || !Number.isFinite(candidate?.daysAway)) {
      throw new TypeError('候选必须含 date、ganzhi 和 daysAway');
    }
    if (!candidate.rule) throw new TypeError('候选必须含 rule');
    let merged = byDate.get(candidate.date);
    if (!merged) {
      merged = {
        date: candidate.date,
        ganzhi: candidate.ganzhi,
        daysAway: candidate.daysAway,
        rules: [],
        _ruleKeys: new Set()
      };
      byDate.set(candidate.date, merged);
    } else if (merged.ganzhi !== candidate.ganzhi || merged.daysAway !== candidate.daysAway) {
      throw new RangeError(`同日候选的干支或 daysAway 不一致：${candidate.date}`);
    }
    const source = candidate.source ?? null;
    const key = `${candidate.rule}\u0000${source ?? ''}`;
    if (!merged._ruleKeys.has(key)) {
      merged._ruleKeys.add(key);
      merged.rules.push({ rule: candidate.rule, source });
    }
  }
  return [...byDate.values()]
    .sort((a, b) => a.daysAway - b.daysAway || a.date.localeCompare(b.date))
    .map(({ _ruleKeys, ...candidate }) => candidate);
}

/** 纯时间尺度标签；阈值见 verdict-thresholds.js，不代表吉凶或置信度。 */
export function timeScale(daysAway) {
  if (!Number.isFinite(daysAway) || daysAway < 0) {
    throw new RangeError(`daysAway 必须是非负有限数，收到 ${daysAway}`);
  }
  if (daysAway <= TIME_SCALE_THRESHOLDS.dayMax) return 'day';
  if (daysAway <= TIME_SCALE_THRESHOLDS.monthMax) return 'month';
  return 'year';
}
